import type { ReactElement } from "react";
import { Banner, Flex, Text } from "@jsq/ds/core";

export const INVALID_ARENA_TITLE = "Missing JSQ arena ID";

const singleInvalidArenaError = (entityName: string, arenaName: string) =>
  `There is no JSQ arena ID provided for the ${entityName} legal entity in the GL.
In order to sync transactions for ${entityName} in ${arenaName},
you will need to populate the JSQ Arena ID custom UDF on the legal entity in Investran with the correct JSQ arena ID of the corresponding GP workspace.`;

const multipleInvalidArenaError = (arenaName: string, arenaId: number) =>
  `There is no JSQ arena ID provided for one or more legal entities in the GL for the ${arenaName} workspace. In order to sync transactions for these entities in ${arenaName}, you will need to populate the JSQ arena ID ${arenaId} on the legal entities in Investran. Please reach out to the FA Ops team for assistance.`;

type InvalidArenaBannerProps = {
  arenaName: string;
  arenaId: number;
  affectedEntityNames: string[];
};

export const InvalidArenaBanner = ({
  arenaName,
  arenaId,
  affectedEntityNames = [],
}: InvalidArenaBannerProps): ReactElement => {
  // Fall back to the generic multi-entity copy unless we have exactly one named
  // entity, so the banner never renders a blank entity name.
  const isSingle = affectedEntityNames.length === 1;
  const descriptionText = isSingle
    ? singleInvalidArenaError(affectedEntityNames[0], arenaName)
    : multipleInvalidArenaError(arenaName, arenaId);

  const description = isSingle ? (
    descriptionText
  ) : (
    <Flex flexDirection="column" gap={4}>
      <Text>{descriptionText}</Text>
      {affectedEntityNames.map((name) => (
        <Text key={name}>{name}</Text>
      ))}
    </Flex>
  );

  return <Banner variant="warning" title={INVALID_ARENA_TITLE} description={description} />;
};
